import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { Truck, Car, Caravan, Plus, Pencil, Trash2, UserCheck } from 'lucide-react';

const TYPE_META = {
  mini_van: { label: 'Мини-фургон', Icon: Truck,   tint: 'text-emerald-600 bg-emerald-50' },
  car:      { label: 'Легковая',    Icon: Car,     tint: 'text-blue-600 bg-blue-50' },
  truck:    { label: 'Большой фургон', Icon: Caravan, tint: 'text-orange-600 bg-orange-50' },
};

const STATUS_META = {
  active: { label: 'Активна',   variant: 'default',   className: 'bg-emerald-600' },
  repair: { label: 'В ремонте', variant: 'secondary', className: 'bg-amber-500 text-white' },
  off:    { label: 'Выходной',  variant: 'outline',   className: 'border-gray-400 text-gray-600' },
};

const emptyForm = {
  name: '', vehicle_type: 'car', capacity_containers: 10,
  plate_number: '', driver_name: '', driver_phone: '', status: 'active',
  default_driver_id: '',
};

const NO_DRIVER = '__none__';

export default function VehiclesSection() {
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // null | {} (new) | vehicle
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchAll = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const [vRes, dRes] = await Promise.all([
        axios.get(`${API}/admin/vehicles`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/drivers`, { headers: getAuthHeaders() }),
      ]);
      setVehicles(vRes.data.vehicles || []);
      setDrivers(dRes.data.drivers || []);
    } catch (e) {
      toast.error('Не удалось загрузить машины');
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const openNew = () => { setForm(emptyForm); setEditing({}); };
  const openEdit = (v) => {
    setForm({
      name: v.name || '',
      vehicle_type: v.vehicle_type || 'car',
      capacity_containers: v.capacity_containers ?? 10,
      plate_number: v.plate_number || '',
      driver_name: v.driver_name || '',
      driver_phone: v.driver_phone || '',
      status: v.status || 'active',
      default_driver_id: v.default_driver_id || '',
    });
    setEditing(v);
  };
  const close = () => { setEditing(null); setForm(emptyForm); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Введите название машины'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        vehicle_type: form.vehicle_type,
        capacity_containers: Math.max(1, parseInt(form.capacity_containers) || 1),
        plate_number: form.plate_number.trim() || null,
        driver_name: form.driver_name.trim() || null,
        driver_phone: form.driver_phone.trim() || null,
        status: form.status,
        default_driver_id: form.default_driver_id || null,
      };
      const driverObj = (id) => {
        const d = drivers.find(x => x.id === id);
        return d ? { id: d.id, name: d.name, is_bound: d.is_bound } : null;
      };
      if (editing && editing.id) {
        const res = await axios.patch(`${API}/admin/vehicles/${editing.id}`, payload, { headers: getAuthHeaders() });
        const merged = { ...res.data, driver: driverObj(res.data.default_driver_id) };
        setVehicles(prev => prev.map(v => (v.id === editing.id ? merged : v)));  // optimistic
        toast.success('Машина обновлена');
      } else {
        const res = await axios.post(`${API}/admin/vehicles`, payload, { headers: getAuthHeaders() });
        const merged = { ...res.data, driver: driverObj(res.data.default_driver_id) };
        setVehicles(prev => [...prev, merged]);  // optimistic
        toast.success('Машина добавлена');
      }
      close();
      fetchAll(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Удалить «${v.name}»?`)) return;
    setVehicles(prev => prev.filter(x => x.id !== v.id));  // optimistic
    try {
      await axios.delete(`${API}/admin/vehicles/${v.id}`, { headers: getAuthHeaders() });
      toast.success('Удалено');
      fetchAll(false);
    } catch {
      toast.error('Ошибка удаления');
      fetchAll(false);
    }
  };

  const totalCapacity = vehicles
    .filter(v => v.status === 'active')
    .reduce((sum, v) => sum + (v.capacity_containers || 0), 0);

  return (
    <Card data-testid="vehicles-section">
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Truck className="w-4 h-4" /> Машины ({vehicles.length})
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Общая активная вместимость: <b>{totalCapacity}</b> контейнеров.
              Используется Telegram-ботом для авто-сплита маршрута.
            </p>
          </div>
          <Button onClick={openNew} size="sm" data-testid="add-vehicle-btn" className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-1" /> Добавить машину
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-muted-foreground py-4">Загрузка...</div>
        ) : vehicles.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            Машин пока нет. Нажмите «Добавить машину».
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {vehicles.map(v => {
              const meta = TYPE_META[v.vehicle_type] || TYPE_META.car;
              const stat = STATUS_META[v.status] || STATUS_META.active;
              const Icon = meta.Icon;
              return (
                <div
                  key={v.id}
                  data-testid={`vehicle-card-${v.id}`}
                  className="border rounded-lg p-4 bg-white hover:shadow-md transition-shadow flex flex-col gap-2"
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-md ${meta.tint}`}>
                      <Icon className="w-7 h-7" strokeWidth={1.6} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{v.name}</div>
                      <div className="text-xs text-muted-foreground">{meta.label}</div>
                    </div>
                    <Badge className={stat.className}>{stat.label}</Badge>
                  </div>
                  <div className="text-2xl font-bold text-emerald-700">
                    {v.capacity_containers}
                    <span className="text-sm font-normal text-muted-foreground ml-1">контейнеров</span>
                  </div>
                  {(v.plate_number || v.driver_name || v.driver_phone) && (
                    <div className="text-xs text-muted-foreground space-y-0.5 pt-1 border-t">
                      {v.plate_number && <div>№ {v.plate_number}</div>}
                      {v.driver_name && <div>Водитель: {v.driver_name}</div>}
                      {v.driver_phone && <div>{v.driver_phone}</div>}
                    </div>
                  )}
                  {v.driver ? (
                    <div className={`text-xs flex items-center gap-1 rounded px-2 py-1 ${v.driver.is_bound ? 'text-emerald-700 bg-emerald-50' : 'text-amber-700 bg-amber-50'}`}>
                      <UserCheck className="w-3.5 h-3.5" />
                      TG-водитель: {v.driver.name}{v.driver.is_bound ? ' ✓' : ' (не привязан)'}
                    </div>
                  ) : (
                    <div className="text-xs flex items-center gap-1 text-gray-400 rounded px-2 py-1">
                      <UserCheck className="w-3.5 h-3.5" /> TG-водитель не назначен
                    </div>
                  )}
                  <div className="flex gap-2 pt-2 mt-auto">
                    <Button
                      variant="outline" size="sm" className="flex-1"
                      onClick={() => openEdit(v)}
                      data-testid={`edit-vehicle-${v.id}`}
                    >
                      <Pencil className="w-3 h-3 mr-1" /> Изменить
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => remove(v)}
                      data-testid={`delete-vehicle-${v.id}`}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid="vehicle-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Изменить машину' : 'Новая машина'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs text-muted-foreground">Название *</label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Например: Мини-фургон, Lexus..."
                data-testid="form-vehicle-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Тип</label>
                <Select value={form.vehicle_type} onValueChange={v => setForm({ ...form, vehicle_type: v })}>
                  <SelectTrigger data-testid="form-vehicle-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_META).map(([k, m]) => (
                      <SelectItem key={k} value={k}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Вместимость (контейнеров) *</label>
                <Input
                  type="number" min="1" max="99999"
                  value={form.capacity_containers}
                  onChange={e => setForm({ ...form, capacity_containers: e.target.value })}
                  data-testid="form-vehicle-capacity"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Гос. номер</label>
                <Input
                  value={form.plate_number}
                  onChange={e => setForm({ ...form, plate_number: e.target.value })}
                  placeholder="123ABC02"
                  data-testid="form-vehicle-plate"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Статус</label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="form-vehicle-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_META).map(([k, m]) => (
                      <SelectItem key={k} value={k}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Водитель</label>
              <Input
                value={form.driver_name}
                onChange={e => setForm({ ...form, driver_name: e.target.value })}
                placeholder="Имя"
                data-testid="form-driver-name"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Телефон водителя</label>
              <Input
                value={form.driver_phone}
                onChange={e => setForm({ ...form, driver_phone: e.target.value })}
                placeholder="+7..."
                data-testid="form-driver-phone"
              />
            </div>
            <div className="rounded-lg border p-3 bg-emerald-50/40">
              <label className="text-xs font-medium text-emerald-800">Telegram-водитель (маршрут в личке)</label>
              <Select
                value={form.default_driver_id || NO_DRIVER}
                onValueChange={v => setForm({ ...form, default_driver_id: v === NO_DRIVER ? '' : v })}
              >
                <SelectTrigger data-testid="form-vehicle-tg-driver" className="mt-1 bg-white">
                  <SelectValue placeholder="Не назначен" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_DRIVER}>Не назначен</SelectItem>
                  {drivers.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}{d.is_bound ? ' ✓' : ' (не привязан)'}{d.is_dispatcher ? ' • диспетчер' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">
                Маршрут этой машины откроется лично у выбранного водителя в Telegram-боте.
                Добавьте и привяжите водителей в разделе «Водители» ниже.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} data-testid="vehicle-cancel">Отмена</Button>
            <Button onClick={save} disabled={saving} data-testid="vehicle-save" className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? 'Сохраняю...' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
